// ABOUTME: Error boundary component that catches runtime errors and displays a fallback UI
// ABOUTME: Shows user-friendly error message with back button, and auto-submits the error to the backend

import React, { useEffect } from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { tokens } from '../../theme';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSubmitErrorMutation } from '../../newapi/global/errorSlice';
import { selectUser } from '../../newapi/user/authentication/authSlice';
import Shbutton from '../theme/buttons/Shbutton';
import Text from '../theme/text/Text';
import Subheading from '../theme/text/Subheading';
import { ReactComponent as TableError } from '../../styles/svg/tableerror.svg'

const ErrorFallback = ({ error, resetErrorBoundary }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const token = useSelector((state) => state.auth.token);
  const environmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
  const environmentId = useSelector((state) => state.settings.environment.selectedId);
  const environmentName = useSelector((state) => state.settings.environment.selectedEnvironmentName);
  const [submitError] = useSubmitErrorMutation();

  useEffect(() => {
    if (!token) return;

    submitError({
      stacktrace: error?.stack ?? error?.toString() ?? 'Unknown error',
      url: window.location.href,
      user_id: user?.id ?? null,
      user_name: user?.name ?? null,
      environment_type: environmentType ?? null,
      environment_id: environmentId ?? null,
      environment_name: environmentName ?? null,
    });
  }, []);

  const handleGoBack = () => {
    resetErrorBoundary();
    navigate(-1);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        padding: 3,
        backgroundColor: theme.palette.mode === 'dark' ? colors.bg.primary : colors.grey[100],
      }}
    >
      <Box
        sx={{
          maxWidth: '500px',
          textAlign: 'center',
          borderRadius: 5,
          backgroundColor: theme.palette.mode === 'dark' ? colors.grey[200] : colors.grey[0],
          boxShadow: `0px 0px 1px ${colors.grey[200]}`,
          px:3,
          pb:3,
          pt:1.5,
        }}
      >
        <TableError/>

        <Subheading
          sx={{
            mb:1,
          }}
        >
          {t('error_boundary_title', 'Er is iets misgegaan')}
        </Subheading>

        <Text center
          sx={{
            marginBottom: 3,
          }}
        >
          {t('error_boundary_message', 'We konden deze pagina niet laden. Probeer terug te gaan naar de vorige pagina.')}
        </Text>


        <Box
          sx={{
            px:2,
            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
            border:`1px solid ${colors.grey[200]}`,
            borderRadius:3,
            py:0.5,
            mb:1.5,
          }}
        >
          <Text center>
            {t('error_boundary_reported', 'De fout is automatisch doorgestuurd naar Stockhub. We gaan er zo snel mogelijk mee aan de slag.')}
          </Text>
        </Box>
        <Shbutton
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          block
          sx={{
            mb:1,
          }}
        >
          {t('error_boundary_go_back', 'Ga terug')}
        </Shbutton>
      </Box>
    </Box>
  );
};

const ErrorBoundary = ({ children }) => {
  const handleError = (error, errorInfo) => {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
